/**
 * Financial category selection (§9.6 "which category applies, and why"):
 * Category A — salaried, same employer 6+ months; Category B — under 6
 * months; Category D — cash savings; A+D / B+D when combined. The category
 * construct and the 6-month tenure line come from the Appendix FM 1.7
 * guidance (register §4, "Category A minimum tenure").
 *
 * Employment tenure prefers `employer_start` (precise, enables dating) and
 * falls back to the `same_employer_6_months` flag. Unknown employment facts
 * fail closed to PENDING_EVIDENCE.
 */
import { addMonthsISO, compareISODate } from "../dates.js";
import type { CaseFacts } from "../types.js";
import type { AppliesPredicate, EvaluatePredicate } from "./predicate.js";

export type CategoryValue = "A" | "B" | "D" | "A+D" | "B+D";

/** Shared, pure categorisation used by the rule and by applies_when gates. null = not determinable. */
export function categorise(facts: CaseFacts, tenureMonthsFigure: number): CategoryValue | null {
  const route = facts.finances.route;
  if (route === "savings") return "D";

  const employment = facts.finances.sponsor_employment;
  let sixMonths: boolean | null = null;
  if (employment?.employer_start) {
    const tenureReached = addMonthsISO(employment.employer_start, tenureMonthsFigure);
    sixMonths = compareISODate(facts.as_of, tenureReached) >= 0;
  } else if (employment && employment.same_employer_6_months !== null) {
    sixMonths = employment.same_employer_6_months;
  }
  if (sixMonths === null) return null;

  const base = sixMonths ? "A" : "B";
  return route === "combination" ? (`${base}+D` as CategoryValue) : base;
}

export const select_financial_category: EvaluatePredicate = (facts, rule, ctx) => {
  const tenureMonths = numeric(ctx.paramFigure(rule, "tenure_months_figure"));
  const category = categorise(facts, tenureMonths);
  if (category === null) {
    return {
      status: "PENDING_EVIDENCE",
      notes: [
        "How long the sponsor has been with their current employer is needed to determine which income category applies.",
      ],
    };
  }
  return {
    status: "MET",
    value: category,
    notes: [categoryNote(category)],
  };
};

function categoryNote(category: CategoryValue): string {
  switch (category) {
    case "A":
      return "Category A applies: salaried employment with the same employer for 6 months or more.";
    case "B":
      return "Category B applies: less than 6 months with the current employer.";
    case "D":
      return "Category D applies: cash savings.";
    case "A+D":
      return "Category A applies (6 months or more with the same employer), combined with cash savings.";
    case "B+D":
      return "Category B applies (less than 6 months with the current employer), combined with cash savings.";
  }
}

function numeric(value: number | string): number {
  if (typeof value !== "number") throw new Error(`Expected a numeric figure, got: ${value}`);
  return value;
}

/** applies_when gates, driven by the already-evaluated FIN.CATEGORY result. */
const categoryOf = (ctx: Parameters<AppliesPredicate>[2]): string | undefined =>
  ctx.results.get("FIN.CATEGORY")?.value;

export const employment_category_a: AppliesPredicate = (facts, _rule, ctx) =>
  facts.finances.route === "employment" && categoryOf(ctx) === "A";

export const employment_category_b: AppliesPredicate = (facts, _rule, ctx) =>
  facts.finances.route === "employment" && categoryOf(ctx) === "B";

export const savings_only_route: AppliesPredicate = (facts) => facts.finances.route === "savings";

export const combination_route: AppliesPredicate = (facts) =>
  facts.finances.route === "combination";
